/**
 * Low-level pure lexical helpers for the CREATE TYPE / CREATE DOMAIN extractor
 * (VDE-2 Slice 2). Split out of `type.ts` to keep the orchestration core
 * focused — same sibling-split convention as `grant.parse.ts`.
 *
 * Regex-free (project rule VQE-D10): the qualified-name read, modifier
 * consumption, enum-value scan, attribute-list split and base-type slicing
 * are all character-level over already-isolated spans or delegate to
 * `readIdentifier` / `scanTopLevelKeywords` / `findMatchingParen`.
 *
 * The captured shapes are kept byte-faithful to the behavioral contract the
 * removed regex parser upheld (see `parseParkedTypeStatement.test.ts`): the
 * `QualifiedName` always folds lower-case (not engine-folded), `baseType` /
 * attribute `typeRef` are trimmed verbatim substrings, and enum values honour
 * SQL `''` escaping.
 */

import type { ParseOptions, QualifiedName } from '../../../sql-grammar/model';
import { readIdentifier } from '../../scope/identifiers';

import { findMatchingParen, skipWsCommentsLocal } from './shared';

export interface Span {
  readonly s: number;
  readonly e: number;
}

export interface NameRead {
  readonly name: QualifiedName;
  readonly end: number;
}

function isIdentStart(c: string | undefined): boolean {
  return c !== undefined && ((c >= 'A' && c <= 'Z') || (c >= 'a' && c <= 'z') || c === '_');
}

/** Read a `[schema.]name` qualified name at `i`, building a `QualifiedName`
 *  that is byte-faithful to the removed regex parser: lower-cased folds (not
 *  engine-folded), `raw` reconstructed as `head.tail` with no surrounding
 *  whitespace, and `quoted` true when *either* part is quoted. Returns null
 *  when no identifier starts at `i`. Whitespace/comments around the dot are
 *  tolerated (the old regex allowed `\s*\.\s*`). */
export function readTypeName(
  source: string,
  i: number,
  end: number,
  options: ParseOptions,
): NameRead | null {
  const head = readIdentifier(source, i, end, options);
  if (!head || !(head.quoted || isIdentStart(source[i]))) return null;

  const afterHead = skipWsCommentsLocal(source, head.end, end, options);
  if (source[afterHead] === '.') {
    const afterDot = skipWsCommentsLocal(source, afterHead + 1, end, options);
    const tail = readIdentifier(source, afterDot, end, options);
    if (tail && (tail.quoted || isIdentStart(source[afterDot]))) {
      const name: QualifiedName = {
        schema: head.value,
        name: tail.value,
        raw: `${head.raw}.${tail.raw}`,
        quoted: head.quoted || tail.quoted,
        foldedName: tail.value.toLowerCase(),
        foldedSchema: head.value.toLowerCase(),
      };
      return { name, end: tail.end };
    }
  }

  const name: QualifiedName = {
    name: head.value,
    raw: head.raw,
    quoted: head.quoted,
    foldedName: head.value.toLowerCase(),
  };
  return { name, end: head.end };
}

/** Extract the values from an enum body span, honouring SQL `''` → `'`
 *  escaping. Character-level scan — not SQL structure. Mirrors the removed
 *  parser's `parseEnumValues` exactly. */
export function parseEnumValues(source: string, s: number, e: number): readonly string[] {
  const values: string[] = [];
  let i = s;
  while (i < e) {
    if (source[i] === "'") {
      let j = i + 1;
      let buf = '';
      while (j < e) {
        if (source[j] === "'") {
          if (source[j + 1] === "'") { buf += "'"; j += 2; continue; }
          break;
        }
        buf += source[j];
        j++;
      }
      values.push(buf);
      i = j + 1;
    } else {
      i++;
    }
  }
  return values;
}

/** Split an attribute-list body span at paren-depth-0 commas, returning each
 *  trimmed segment's text. Self-contained character-level scan (NOT a wrapper
 *  over scanner.findTopLevelCommas): it advances over quoted identifiers via
 *  `readIdentifier` so a quoted attribute name containing a comma is not
 *  split mid-token, but — like the removed parser's `splitTopLevelCommas` —
 *  it does NOT specially handle mid-list SQL comments. */
function splitTopLevelCommaSegs(
  source: string,
  s: number,
  e: number,
  options: ParseOptions,
): Span[] {
  const commas: number[] = [];
  let depth = 0;
  let i = s;
  while (i < e) {
    const ident = readIdentifier(source, i, e, options);
    if (ident && (ident.quoted || isIdentStart(source[i]))) { i = ident.end; continue; }
    const c = source[i];
    if (c === "'") {
      let j = i + 1;
      while (j < e) {
        if (source[j] === "'") {
          if (source[j + 1] === "'") { j += 2; continue; }
          j++;
          break;
        }
        j++;
      }
      i = j;
      continue;
    }
    if (c === '(') { depth++; i++; continue; }
    // Clamp at 0 on unbalanced ')' (malformed input) so a later top-level comma
    // is still split — matches the Slice-1 grant.parse.ts splitter's guard.
    if (c === ')') { if (depth > 0) depth--; i++; continue; }
    if (c === ',' && depth === 0) { commas.push(i); i++; continue; }
    i++;
  }
  const out: Span[] = [];
  let segStart = s;
  for (const cm of commas) { out.push({ s: segStart, e: cm }); segStart = cm + 1; }
  out.push({ s: segStart, e });
  return out;
}

function trimmedText(source: string, s: number, e: number): string {
  return source.slice(s, e).trim();
}

/** Split a composite/object attribute body into `{ name, typeRef }` records.
 *  Each segment is split on its first whitespace run (mirrors the removed
 *  parser's `entry.search(/\s/)` semantics) — name = leading run, typeRef =
 *  the verbatim trimmed remainder (carried as-is, never further parsed).
 *  Empty segments are dropped, matching the old `.filter(s => s.length>0)`. */
export function parseAttributesBody(
  source: string,
  s: number,
  e: number,
  options: ParseOptions,
): ReadonlyArray<{ readonly name: string; readonly typeRef: string }> {
  const out: { name: string; typeRef: string }[] = [];
  for (const seg of splitTopLevelCommaSegs(source, s, e, options)) {
    const entry = trimmedText(source, seg.s, seg.e);
    if (entry.length === 0) continue;
    let wsIdx = -1;
    for (let k = 0; k < entry.length; k++) {
      const ch = entry[k]!;
      if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r' || ch === '\f' || ch === '\v') {
        wsIdx = k;
        break;
      }
    }
    if (wsIdx < 0) { out.push({ name: entry, typeRef: '' }); continue; }
    out.push({ name: entry.slice(0, wsIdx).trim(), typeRef: entry.slice(wsIdx + 1).trim() });
  }
  return out;
}

/** Read an unsigned integer literal at `i` (skipping leading ws). Returns the
 *  numeric value + the index just past the digits, or null when no digit run
 *  is present. Used for `VARRAY(<n>)`. */
export function readUintAt(
  source: string,
  i: number,
  end: number,
  options: ParseOptions,
): { value: number; end: number } | null {
  let p = skipWsCommentsLocal(source, i, end, options);
  const start = p;
  while (p < end && source[p]! >= '0' && source[p]! <= '9') p++;
  if (p === start) return null;
  return { value: Number(source.slice(start, p)), end: p };
}

export { findMatchingParen, skipWsCommentsLocal };
